"use client";

import { XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { HistorySkeleton } from "@/components/history-skeleton";
import { RecordingButton } from "@/components/recording-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { fetchTurnsPage, type Turn } from "@/lib/console";
import { type DaySection, groupByDay } from "@/lib/history";
import { cn } from "@/lib/utils";

const OLDER_FAILED_TEXT = "Earlier messages couldn't load. Try again.";

export function ChatHistory({ deviceId }: { deviceId: string }) {
  // Kept newest-first, the order the API pages in; older pages append at the
  // end. null = the first page hasn't arrived yet (the skeleton state).
  const [turns, setTurns] = useState<Turn[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [initialFailed, setInitialFailed] = useState(false);
  // Bumped by "Try again" to re-run the initial-load effect.
  const [retryToken, setRetryToken] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderFailed, setOlderFailed] = useState(false);
  // Day shown in the right pane; null means the newest day, so no state is
  // needed until the user actually picks one.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const paneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The page's only data source, fetched in the browser after hydration
    // (the exported HTML ships the skeleton). Stale responses are dropped so
    // a retry can't be overtaken by the request it replaces.
    let stale = false;
    fetchTurnsPage(deviceId).then(
      (page) => {
        if (stale) return;
        setTurns(page.items);
        setNextCursor(page.next_cursor);
        setInitialFailed(false);
      },
      (error: unknown) => {
        if (stale) return;
        // The inline error state is deliberately generic; the console carries
        // the cause — a missing NEXT_PUBLIC_API_ORIGIN, a CORS misconfig, and a
        // down backend are indistinguishable without it.
        console.error("initial history load failed:", error);
        setInitialFailed(true);
      },
    );
    return () => {
      stale = true;
    };
  }, [deviceId, retryToken]);

  useEffect(() => {
    // A newly picked day starts reading at its beginning.
    if (paneRef.current) paneRef.current.scrollTop = 0;
  }, [selectedKey]);

  if (initialFailed) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-4 text-center">
        <h2 className="font-semibold text-sm">The history couldn&apos;t load</h2>
        <p className="max-w-sm text-muted-foreground text-sm">
          {process.env.NODE_ENV === "development" ? (
            <>
              Check that the backend is running (<code>docker compose up</code> in{" "}
              <code>backend/</code>), then try again.
            </>
          ) : (
            "Something went wrong. Check your connection and try again."
          )}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Clearing the flag here (not in the effect) shows the skeleton
            // while the retry is in flight.
            setInitialFailed(false);
            setRetryToken((token) => token + 1);
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (turns === null) return <HistorySkeleton />;

  if (turns.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
        <span aria-hidden className="text-2xl text-spark">
          ✦
        </span>
        <p className="max-w-xs text-muted-foreground text-sm">
          Nothing here yet. When your child talks to First Magic, every question and answer shows up
          here.
        </p>
      </div>
    );
  }

  async function loadOlder() {
    if (!nextCursor) return;
    setLoadingOlder(true);
    setOlderFailed(false);
    try {
      const page = await fetchTurnsPage(deviceId, nextCursor);
      setTurns((current) => [...(current ?? []), ...page.items]);
      setNextCursor(page.next_cursor);
    } catch {
      // Error boundaries can't see event-handler errors — show it here.
      setOlderFailed(true);
    } finally {
      setLoadingOlder(false);
    }
  }

  // groupByDay wants chronological input; the day list shows newest first.
  const sections = groupByDay([...turns].reverse());
  const days = [...sections].reverse();
  const selected = days.find((day) => day.key === selectedKey) ?? days[0];

  return (
    <div className="flex h-full">
      {/* Both scroll panes are `relative` so absolutely-positioned descendants
          (the bubbles' sr-only prefixes) get their containing block inside the
          pane and clip with it — otherwise they anchor to the document at
          their unscrolled y-position and stretch it, making the page itself
          scrollable (wheel gestures then land on the window, not the pane). */}
      <aside
        aria-label="Days"
        className="relative hidden w-64 shrink-0 flex-col overflow-y-auto border-r md:flex"
      >
        <DayList days={days} selectedKey={selected.key} onSelect={setSelectedKey} />
        {(nextCursor || olderFailed) && (
          <div className="flex flex-col gap-1 px-3 pb-3">
            {nextCursor && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full font-mono text-[11px] text-muted-foreground"
                disabled={loadingOlder}
                onClick={loadOlder}
              >
                {loadingOlder ? "Loading…" : "Show earlier"}
              </Button>
            )}
            {olderFailed && (
              <p role="alert" className="text-center text-destructive text-xs">
                {OLDER_FAILED_TEXT}
              </p>
            )}
          </div>
        )}
      </aside>

      <div ref={paneRef} className="relative min-w-0 flex-1 overflow-y-auto">
        {/* On mobile the day list collapses into this chip strip. */}
        <div className="sticky top-0 z-10 border-b bg-background/90 backdrop-blur md:hidden">
          <div className="flex gap-2 overflow-x-auto px-4 py-2.5">
            {days.map((day) => (
              <button
                key={day.key}
                type="button"
                onClick={() => setSelectedKey(day.key)}
                aria-current={day.key === selected.key || undefined}
                className={cn(
                  "shrink-0 whitespace-nowrap rounded-full border px-3 py-1 font-mono text-[11px] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                  day.key === selected.key
                    ? "border-spark/40 bg-spark/10"
                    : "text-muted-foreground hover:bg-muted/60",
                )}
              >
                {day.label}
              </button>
            ))}
            {nextCursor && (
              <button
                type="button"
                disabled={loadingOlder}
                onClick={loadOlder}
                className="shrink-0 whitespace-nowrap rounded-full border border-dashed px-3 py-1 font-mono text-[11px] text-muted-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                {loadingOlder ? "Loading…" : "Earlier…"}
              </button>
            )}
          </div>
          {olderFailed && (
            <p role="alert" className="px-4 pb-2 text-destructive text-xs">
              {OLDER_FAILED_TEXT}
            </p>
          )}
        </div>

        {/* The sidebar already shows the short label ("Today", "4 Aug") — the
            pane header states just the full date, once. */}
        <header className="sticky top-0 z-10 hidden border-b bg-background/90 backdrop-blur md:block">
          <div className="mx-auto max-w-3xl px-4 py-3 text-right">
            <h2 className="font-mono text-[11px] text-muted-foreground">{fullDate(selected)}</h2>
          </div>
        </header>

        <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 pt-6 pb-10">
          {selected.items.map((turn) => (
            <TurnRow key={turn.id} turn={turn} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayList({
  days,
  selectedKey,
  onSelect,
}: {
  days: DaySection[];
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  return (
    <ul className="flex flex-col gap-1 p-3">
      {days.map((day) => {
        const latest = day.items[day.items.length - 1];
        const isSelected = day.key === selectedKey;
        return (
          <li key={day.key}>
            <button
              type="button"
              onClick={() => onSelect(day.key)}
              aria-current={isSelected || undefined}
              className={cn(
                "flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left outline-none transition-colors",
                "hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50",
                isSelected && "bg-muted",
              )}
            >
              <span className="flex w-full items-baseline justify-between gap-2">
                <span className="truncate font-medium text-sm">{day.label}</span>
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                  {timeLabel(latest.output_received_at)}
                </span>
              </span>
              {/* Mail-style preview: the day's most recent question. Correct
                  even for a partially loaded day, because pages arrive
                  newest-first. */}
              <span className="w-full truncate text-muted-foreground text-xs">
                {latest.user.text}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// Long-form date for the pane header, e.g. "Wednesday, August 6".
function fullDate(day: DaySection): string {
  const date = new Date(day.items[0].input_received_at);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(date.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
  });
}

// The day list shows minute precision; bubbles show seconds so rapid-fire
// questions in one minute stay distinguishable.
function timeLabel(iso: string, precision: "minutes" | "seconds" = "minutes"): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    ...(precision === "seconds" && { second: "2-digit" }),
  });
}

// Click-to-enlarge for the photo a question came with. The full view reuses
// the thumbnail's URL, so the browser's immutable media cache makes it instant.
function PhotoLightbox({ src }: { src: string }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="cursor-zoom-in rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          {/* aspect-[9/16] matches the device's portrait stills (hw/camera.py
              capture_jpeg rotates to portrait), so the box is exact and lazy
              loads shift nothing. Console media comes from the API origin
              (absolute URLs) and is immutable-cached; next/image's optimizer
              is unavailable in a static export anyway. */}
          {/* biome-ignore lint/performance/noImgElement: see above — no optimizer in a static export */}
          <img
            src={src}
            alt="Sent with this question"
            loading="lazy"
            className="aspect-[9/16] w-56 max-w-full rounded-lg object-cover"
          />
        </button>
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        // w-max, not w-fit: the content is centered via left-1/2 + translate,
        // so fit-content would shrink against only the right half of the
        // viewport and cap the photo at 50vw.
        className="w-max overflow-hidden p-0 sm:max-w-[calc(100%-4rem)]"
      >
        <DialogTitle className="sr-only">Photo, full size</DialogTitle>
        {/* biome-ignore lint/performance/noImgElement: no optimizer in a static export */}
        <img
          src={src}
          alt="Sent with this question, full size"
          className="max-h-[85dvh] max-w-full"
        />
        {/* The stock close button sits on the photo itself, so it needs its
            own scrim to stay visible on bright images. */}
        <DialogClose asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="absolute top-2 right-2 rounded-full bg-black/40 text-white hover:bg-black/55 hover:text-white"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}

// One exchange, chronological: the kid's bubble, then the answer bubble.
function TurnRow({ turn }: { turn: Turn }) {
  return (
    <>
      <div className="flex max-w-[85%] flex-col items-end gap-1 self-end">
        <div className="flex flex-col gap-2 rounded-2xl rounded-br-sm border border-spark/25 bg-spark/10 px-4 py-2.5">
          {turn.user.image_url && <PhotoLightbox src={turn.user.image_url} />}
          <p className="text-sm leading-relaxed">
            {/* Who said what is otherwise only visual (alignment and tint). */}
            <span className="sr-only">Your child asked: </span>
            {turn.user.text}
          </p>
        </div>
        <span className="flex items-center gap-0.5 font-mono text-[11px] text-muted-foreground">
          <RecordingButton src={turn.user.audio_url} transcript={turn.user.text} />
          {timeLabel(turn.input_received_at, "seconds")}
        </span>
      </div>
      <div className="flex max-w-[85%] flex-col items-start gap-1 self-start">
        <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-sm leading-relaxed">
          <span className="sr-only">First Magic answered: </span>
          {turn.model.text}
        </div>
        <span className="pl-1 font-mono text-[11px] text-muted-foreground">
          {timeLabel(turn.output_received_at, "seconds")}
        </span>
      </div>
    </>
  );
}
